import { useCallback } from "react";
import { useLocation } from "react-router-dom";
import { trackEvent } from "../services/app-insights.service";
import { useWorkflowIntelligence } from "../contexts/WorkflowIntelligenceContext";

/**
 * React hook returning a `track` callback that emits an App Insights custom
 * event with the addin's standard context (page, userRole, spreadsheetId)
 * auto-attached. Use this from any component instead of calling
 * `trackEvent()` directly — keeps the property shape uniform across queries.
 *
 * Non-React code (background callbacks, services) can still call the raw
 * `trackEvent` export, just without the auto-attached context.
 *
 * ## Naming convention
 * Events use lowercase `feature.action` form (with optional `_qualifier`):
 *   - `setup.saved`
 *   - `status_item.status_changed`
 *   - `discussion.comment_added`
 *   - `task.created`
 *   - `xero.trial_balance_imported`
 *   - `signoff.partner_signed`
 *
 * ## What NOT to put in properties
 * App Insights persists property values verbatim. Don't send PII:
 *   - emails, names, raw user input (comment text, document text)
 *   - workbook contents (cell values, formulas)
 * Safe to send: spreadsheetId, statusItemType, jobType, taskDefinitionId,
 * counts, durations, success/failure booleans.
 *
 * ## V1 event catalogue (suggested first-pass list)
 *   setup.saved                         { jobType }
 *   status_item.created                 { type }
 *   status_item.status_changed          { from, to, type }
 *   discussion.created                  { type, tagCount }
 *   discussion.comment_added            { discussionType }
 *   document.uploaded                   { type? }
 *   task.created                        { taskDefinitionId?, outputType }
 *   xero.trial_balance_imported         { tenantId }
 *   signoff.manager_signed              {}
 *   signoff.partner_signed              {}
 *   completion_certificate.generated    {}
 * Add more as feature usage questions come up.
 */
export function useTrackEvent() {
    const { userRole, spreadsheetId } = useWorkflowIntelligence();
    const location = useLocation();

    return useCallback(
        (name: string, properties?: Record<string, unknown>) => {
            trackEvent(name, {
                page: location.pathname,
                userRole,
                // spreadsheetId is null on routes that aren't bound to a
                // workbook (e.g. settings). Omit when null so the column
                // doesn't fill with empty strings in queries.
                ...(spreadsheetId ? { spreadsheetId } : {}),
                ...properties,
            });
        },
        [userRole, spreadsheetId, location.pathname],
    );
}
